import { selectCard } from 'js/actions';
import { AvailableFilters } from 'js/components/MacroInput/types/AvailableFilters';
import { API_KEY_RESOURCE_TYPES } from 'js/constants/resourceTypes';
import { SearchFilterViewsType } from 'js/constants/visibilityFilters';
import React, { ReactNode, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { has, isNil } from 'lodash';
import DateUtils from 'js/lib/DateUtils';
import { ChipWithCopy } from 'new/elements/ChipWithCopy';
import DataCard, { DataCardCell, NameDescCell } from 'new/elements/DataCard';
import DataCardList from 'new/elements/DataCardList';
import LightningStatus from 'new/elements/LightningStatus';
import Typography from 'new/elements/TypographySupport/TypographySupport';
import { Stack } from 'new/elements/Stack';
import { useListOldReduxContainer } from 'new/elements/DataCard/useListOldReduxContainer';
import { mapStateToProps } from 'js/components/data_sets/cards/stateMapper';
import APIKeyDetailActionsContainer from 'js/components/apiKeys/cards/APIKeyDetailActionsContainer';
import { APIKeyDetailHeader } from 'js/components/apiKeys/cards/APIKeyDetailHeader';
import { DetailsIconButton } from 'new/elements/Button';

type Props = {
  urlId?: string;
  data: any[];
  isLoading: boolean;
  availableFilterTypes: string[];
  view: SearchFilterViewsType;
  detailViewTabs: any[];
  loadData: (
    page: number,
    sortCriteria: string,
    availableFilters: AvailableFilters,
    options?: { aclQueryMode?: string },
  ) => void;
  noContentView?: ReactNode;
};

const makeRowRenderer =
  ({ handleCardSelected, getData }) =>
  (index, item) => {
    const { cardData, name, description, createdAt, status, keyTypeDisplayName } = getData(index);

    const onClickCard = () => handleCardSelected(cardData);

    return (
      <DataCard
        key={item.id || index}
        onClick={onClickCard}
        rowsSlots={[
          <>
            <NameDescCell name={name} description={description} />
            <DataCardCell rightCTAsCell>
              <LightningStatus status={status} />
              <DetailsIconButton onClick={onClickCard} />
              <APIKeyDetailActionsContainer data={cardData} openDetailsModal={onClickCard} />
            </DataCardCell>
          </>,
          <DataCardCell multiCol>
            <Stack spacing="8px" direction="row" alignItems="flex-end">
              <ChipWithCopy tooltipTitle="Copy API Key ID" toCopy={cardData.id}>
                ID {cardData.id}
              </ChipWithCopy>
              <Typography variant="body3">
                <b>CREATED ON:</b> {createdAt}
              </Typography>
            </Stack>
            {keyTypeDisplayName && (
              <Typography color="primary" variant="body3">
                <b>KEY TYPE:</b> {keyTypeDisplayName}
              </Typography>
            )}
          </DataCardCell>,
        ].filter(Boolean)}
      />
    );
  };

const APIKeysTable = ({
  availableFilterTypes,
  view,
  urlId,
  isLoading,
  data,
  detailViewTabs,
  loadData,
  noContentView,
}: Props) => {
  const dispatch = useDispatch();
  const listing = useSelector((state: any) => state.app.listing);
  const { selectedCardId } = listing;
  const handleCardSelected = useCallback((row) => {
    dispatch(selectCard(row.id));
  }, []);
  useEffect(() => {
    if (urlId) {
      handleCardSelected({ id: urlId });
    }
  }, []);

  const getData = useListOldReduxContainer(data, mapStateToProps, (ownProps: { cardData: any }) => {
    const { cardData } = ownProps;
    const createdAt = DateUtils.convertDateStringToFormat(cardData.created_at);
    const name = cardData.name || '(Untitled)';
    const description = cardData.description || ' ';

    let keyTypeDisplayName =
      !isNil(cardData.resourceType) && has(API_KEY_RESOURCE_TYPES, cardData.resourceType)
        ? `${API_KEY_RESOURCE_TYPES[cardData.resourceType].displayName} Key`
        : '';

    return {
      cardData,
      status: cardData.status,
      name,
      description,
      createdAt,
      keyTypeDisplayName,
    };
  });

  return (
    <DataCardList
      data={data}
      view={view}
      searchPlaceholder="Search for IDs, names, and more..."
      availableFilterTypes={availableFilterTypes}
      detailViewTabs={detailViewTabs}
      urlId={urlId}
      loading={isLoading}
      enableSorting={false}
      detailHeaderContent={
        <APIKeyDetailHeader
          {...getData(data.findIndex((item: any) => item.id === selectedCardId))}
        />
      }
      rowRenderer={makeRowRenderer({ handleCardSelected, getData })}
      noContentView={noContentView}
      loadData={loadData}
    />
  );
};

export default APIKeysTable;
